import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Card, CardMedia, CardContent, CircularProgress,
  Dialog, DialogContent, IconButton, Button, useTheme, useMediaQuery
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import { db } from './services/firebase';
import { collection, getDocs } from 'firebase/firestore';
import { Document, Page, pdfjs } from 'react-pdf';

pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.js';

export default function PromoPage() {
  const [promoList, setPromoList] = useState<any[]>([]);
  const [volantini, setVolantini] = useState<any[]>([]);
  const [selectedVolantino, setSelectedVolantino] = useState<any>(null);
  const [numPages, setNumPages] = useState<number>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [openVolantinoDialog, setOpenVolantinoDialog] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [touchStartX, setTouchStartX] = useState<number | null>(null);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    setLoading(true);
    setError(null);

    const fetchPromo = async () => {
      try {
        const promoCollection = collection(db, 'promo');
        const snapshot = await getDocs(promoCollection);
        const promos = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setPromoList(promos);
      } catch (err: any) {
        setError('Errore caricamento promo: ' + err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchPromo();

    // Carica volantini PDF
    getDocs(collection(db, 'volantini')).then(snapshot => {
      const docs = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setVolantini(docs);
      if (docs.length > 0) setSelectedVolantino(docs[0]);
    });
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [selectedVolantino]);

  const handleOpenDialog = (imageUrl: string) => {
    setSelectedImage(imageUrl);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedImage(null);
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchStartX(e.touches[0].clientX);
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    if (touchStartX === null) return;
    const touchEndX = e.changedTouches[0].clientX;
    const diff = touchStartX - touchEndX;
    if (diff > 50 && currentPage < numPages!) {
      setCurrentPage(currentPage + 1);
    } else if (diff < -50 && currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
    setTouchStartX(null);
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        p: { xs: 1, sm: 2 },
        width: '100%',
        maxWidth: 900,
        margin: '0 auto',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        bgcolor: 'linear-gradient(135deg, #e3eafc 0%, #f5f7fa 100%)',
        boxSizing: 'border-box',
        overflowX: 'hidden',
      }}
    >
      {/* --- SEZIONE PROMO IMMAGINI --- */}
      <Box
        sx={{
          width: '100%',
          maxWidth: 600,
          bgcolor: '#fff',
          borderRadius: { xs: 2, sm: 4 },
          boxShadow: 6,
          p: { xs: 1, sm: 3 },
          mt: { xs: 1, sm: 3 },
          mb: { xs: 2, sm: 4 },
          minHeight: 120,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          boxSizing: 'border-box',
          overflowX: 'hidden',
        }}
      >
        <Typography
          variant={isMobile ? "h6" : "h5"}
          component="h1"
          align="center"
          sx={{
            mb: 3,
            color: '#1e3c72',
            fontWeight: 700,
            letterSpacing: 1,
            textShadow: '0 2px 8px #e3eafc'
          }}
        >
          Promo X Te!
        </Typography>

        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
            <CircularProgress color="primary" />
          </Box>
        )}

        {error && (
          <Typography color="error" align="center" sx={{ mt: 4, bgcolor: '#fff', p: 2, borderRadius: 2 }}>
            {error}
          </Typography>
        )}

        {!loading && !error && (
          <Box sx={{ width: '100%', boxSizing: 'border-box' }}>
            {promoList.map((promo) => (
              <Card
                key={promo.id}
                sx={{
                  width: '100%',
                  maxWidth: 500,
                  mx: 'auto',
                  mb: 2,
                  borderRadius: 4,
                  boxShadow: 6,
                  cursor: promo.image ? 'pointer' : 'default',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  '&:hover': {
                    transform: promo.image ? 'scale(1.01)' : 'none',
                    boxShadow: promo.image ? 12 : 6,
                  },
                  bgcolor: '#f5f7fa',
                  display: 'flex',
                  flexDirection: isMobile ? 'column' : 'row',
                  alignItems: 'center',
                  p: 1,
                  boxSizing: 'border-box',
                  overflowX: 'hidden',
                }}
                onClick={() => promo.image && handleOpenDialog(promo.image)}
              >
                {promo.image && (
                  <CardMedia
                    component="img"
                    image={promo.image}
                    alt="Promo"
                    sx={{
                      width: isMobile ? '100%' : 140,
                      maxWidth: '100%',
                      height: isMobile ? 120 : 120,
                      objectFit: 'cover',
                      borderRadius: 3,
                      mr: isMobile ? 0 : 2,
                      mb: isMobile ? 1 : 0,
                    }}
                  />
                )}
                <CardContent sx={{ flex: 1 }}>
                  <Typography variant="body1" sx={{ fontWeight: 600, color: '#1e3c72', textAlign: isMobile ? 'center' : 'left' }}>
                    {promo.testo}
                  </Typography>
                </CardContent>
              </Card>
            ))}
            {promoList.length === 0 && (
              <Typography variant="body2" align="center" sx={{ width: '100%', mt: 4, color: '#1e3c72' }}>
                Nessuna promo disponibile.
              </Typography>
            )}
          </Box>
        )}
      </Box>

      {/* --- SEZIONE VOLANTINI PDF --- */}
      <Box
        sx={{
          width: '100%',
          maxWidth: 600,
          bgcolor: '#fff',
          borderRadius: { xs: 2, sm: 4 },
          boxShadow: 6,
          p: { xs: 1, sm: 3 },
          mb: 4,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          boxSizing: 'border-box',
          overflowX: 'hidden',
        }}
      >
        <Typography variant={isMobile ? "h6" : "h5"} sx={{ mb: 2, color: '#1e3c72', fontWeight: 700 }}>
          Volantini
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap', justifyContent: 'center' }}>
          {volantini.map(v => (
            <Button
              key={v.id}
              variant={selectedVolantino?.id === v.id ? 'contained' : 'outlined'}
              onClick={() => setSelectedVolantino(v)}
              sx={{
                fontWeight: 600,
                borderRadius: 3,
                bgcolor: selectedVolantino?.id === v.id ? '#2a5298' : '#e3eafc',
                color: selectedVolantino?.id === v.id ? '#fff' : '#2a5298',
                boxShadow: selectedVolantino?.id === v.id ? 4 : 1,
                mb: 1,
                px: 2,
                minWidth: 120,
                transition: 'all 0.2s'
              }}
            >
              {v.nome}
            </Button>
          ))}
        </Box>
        {selectedVolantino && (
          <Box sx={{
            width: '100%',
            maxWidth: 500,
            mx: 'auto',
            bgcolor: '#f5f7fa',
            borderRadius: 3,
            boxShadow: 2,
            p: 2,
            mb: 2,
            minHeight: 200,
            display: 'flex',
            justifyContent: 'center',
            cursor: 'pointer',
            boxSizing: 'border-box',
            overflowX: 'hidden',
          }}
            onClick={() => setOpenVolantinoDialog(true)}
          >
            <Document
              file={selectedVolantino.url}
              loading={<CircularProgress color="primary" />}
              error={<Typography color="error">Impossibile caricare il PDF.</Typography>}
              onLoadSuccess={({ numPages }) => setNumPages(numPages)}
            >
              <Page
                pageNumber={1}
                width={isMobile ? 180 : 240}
                renderTextLayer={false}
                renderAnnotationLayer={false}
              />
            </Document>
          </Box>
        )}
        {volantini.length === 0 && (
          <Typography variant="body2" align="center" sx={{ width: '100%', mt: 2, color: '#1e3c72' }}>
            Nessun volantino disponibile.
          </Typography>
        )}
      </Box>

      {/* Dialog per immagine promo ingrandita */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="md"
        PaperProps={{
          sx: { borderRadius: 4, bgcolor: '#f5f7fa' }
        }}
      >
        <DialogContent sx={{ position: 'relative', p: 0 }}>
          <IconButton
            aria-label="close"
            onClick={handleCloseDialog}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[700],
              backgroundColor: 'rgba(255, 255, 255, 0.7)',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.9)',
              }
            }}
          >
            <CloseIcon />
          </IconButton>
          {selectedImage && (
            <img
              src={selectedImage}
              alt="Promo ingrandita"
              style={{
                width: '100%',
                display: 'block',
                borderRadius: '0 0 32px 32px',
                boxShadow: '0 8px 32px rgba(30,60,114,0.15)'
              }}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Dialog per volantino sfogliabile */}
      <Dialog
        open={openVolantinoDialog}
        onClose={() => setOpenVolantinoDialog(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 4,
            bgcolor: '#f5f7fa',
            p: 2,
            width: '100vw',
            maxWidth: 900,
            height: '90vh',
            m: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }
        }}
      >
        <DialogContent
          sx={{
            position: 'relative',
            p: 0,
            width: '100%',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            overflow: 'hidden'
          }}
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
        >
          <IconButton
            aria-label="close"
            onClick={() => setOpenVolantinoDialog(false)}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[700],
              backgroundColor: 'rgba(255, 255, 255, 0.7)',
              zIndex: 2,
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.9)',
              }
            }}
          >
            <CloseIcon />
          </IconButton>
          {selectedVolantino && (
            <Box sx={{ pt: 6, display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}>
              <Box sx={{ mb: 1, display: 'flex', gap: 1, justifyContent: 'center', alignItems: 'center' }}>
                <IconButton
                  size="small"
                  disabled={currentPage <= 1}
                  onClick={() => setCurrentPage((p) => p - 1)}
                  sx={{ bgcolor: '#e3eafc', '&:hover': { bgcolor: '#b6c6e6' } }}
                >
                  <ArrowBackIosNewIcon fontSize="small" />
                </IconButton>
                <Typography sx={{ mx: 1, fontSize: 14 }}>
                  {currentPage} / {numPages}
                </Typography>
                <IconButton
                  size="small"
                  disabled={currentPage >= numPages!}
                  onClick={() => setCurrentPage((p) => p + 1)}
                  sx={{ bgcolor: '#e3eafc', '&:hover': { bgcolor: '#b6c6e6' } }}
                >
                  <ArrowForwardIosIcon fontSize="small" />
                </IconButton>
              </Box>
              <Document
                file={selectedVolantino.url}
                loading={<CircularProgress color="primary" />}
                error={<Typography color="error">Impossibile caricare il PDF.</Typography>}
              >
                <Page
                  pageNumber={currentPage}
                  width={isMobile ? 320 : 700}
                  renderTextLayer={false}
                  renderAnnotationLayer={false}
                />
              </Document>
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
}

